import type { ReactNode } from "react";
import { Radio as RadioAria } from "react-aria-components";
import type { RadioProps } from "react-aria-components";

import { cn } from "@/lib/helpers/cn";

type RatingOptionProps = {
  label: ReactNode;
} & Omit<RadioProps, "children">;

export function RatingOption({ className, label, ...radioProps }: RatingOptionProps) {
  return (
    <RadioAria
      className={cn(
        "tw:flex! tw:cursor-pointer tw:items-center tw:justify-center tw:mb-0!",
        "tw:font-normal tw:text-center tw:leading-normal",
        "tw:border",
        "tw:max-md:px-3 tw:md:px-6 tw:h-16",
        "tw:max-md:text-xs tw:md:text-md",

        // responsive border rounding and overlap
        "tw:max-md:rounded-none tw:max-md:last:rounded-r-lg tw:max-md:first:rounded-l-lg tw:md:rounded-lg",
        "tw:max-md:not-first:-ml-px",

        // Default state
        "tw:border-stroke-quaternary tw:bg-fill-white tw:text-text-primary",

        // Selected state
        "tw:selected:border-stroke-teal-primary tw:selected:bg-fill-teal-quinary tw:selected:text-text-teal-primary tw:selected:font-semibold",
        "tw:selected:shadow-[inset_0_0_0_1px_var(--tw-color-stroke-teal-primary)]",
        "tw:selected:relative tw:selected:z-10",

        // Hover state (not selected)
        "tw:hover:not-selected:border-stroke-teal-primary",
        "tw:hover:not-selected:relative tw:hover:not-selected:z-10",

        // Focus state
        "tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2",
        "tw:focus-visible:outline-stroke-bright-purple-secondary",

        // Disabled state
        "tw:disabled:opacity-50 tw:disabled:cursor-not-allowed",

        className,
      )}
      {...radioProps}
    >
      {/* Invisible bold text reserves dimensions to prevent layout shift */}
      <div className="tw:relative">
        <span aria-hidden="true" className="tw:invisible tw:font-semibold tw:pointer-events-none">
          {label}
        </span>

        <span className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center">
          {label}
        </span>
      </div>
    </RadioAria>
  );
}
